/*
 * useRunStream — subscribes a run's event timeline: replay historical events
 * (GET /events) then follow live (SSE), feeding both into the pure reducer.
 *
 * Flow (matches AC-7 "refresh/reconnect can fully replay"):
 *   1. fetch backlog in bounded REST pages         → seeds the reducer once
 *   2. open streamRun(after_seq=lastSeq)           → server replays gap + live
 *   3. reducer dedupes by seq, so overlap between (1) and (2) is harmless
 *
 * The reducer's derivedStatus is mirrored into the run-detail query cache so the
 * status header advances live without a separate poll. When the run reaches a
 * terminal status we close the stream so the browser's EventSource does not
 * auto-reconnect forever (the server closes a terminal stream — per docs
 * 11-api.md §2.3 the client must stop reconnecting).
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/ApiProvider';
import type { StreamHandle } from '../api/client';
import { qk } from '../api/queries';
import {
  initialEventState,
  reduceEvents,
  type EventState,
} from '../api/eventReducer';
import { isTerminal, type Run, type RunEvent } from '../api/types';
import { reconcileRunSnapshot, reconcileRunStatus } from '../api/runCache';

export type StreamPhase = 'connecting' | 'live' | 'closed' | 'error';

interface StreamStateWrap {
  runId: string;
  state: EventState;
}

type Action =
  | { kind: 'events'; runId: string; events: RunEvent[] }
  | { kind: 'hydrate'; runId: string; state: EventState }
  | { kind: 'reset'; runId: string };

// The list endpoint caps pages at 1,000. Runs above that threshold previously
// spilled the rest of their history into SSE, where one React dispatch per
// frame repeatedly rebuilt the entire Markdown timeline. Paging REST to
// completion keeps transfers bounded while letting React restore history in
// one commit.
const REPLAY_PAGE_SIZE = 1000;

function reducer(s: StreamStateWrap, a: Action): StreamStateWrap {
  if (a.kind !== 'reset' && a.runId !== s.runId) return s;
  switch (a.kind) {
    case 'events': {
      const next = reduceEvents(s.state, a.events);
      return next === s.state ? s : { runId: a.runId, state: next };
    }
    case 'hydrate':
      return { runId: a.runId, state: a.state };
    case 'reset':
      return { runId: a.runId, state: initialEventState() };
  }
}

export function useRunStream(runId: string, enabled = true) {
  const api = useApi();
  const qc = useQueryClient();
  const [wrap, dispatch] = useReducer(reducer, undefined, () => ({
    runId,
    state: initialEventState(),
  }));
  const [phase, setPhase] = useState<StreamPhase>('connecting');
  // Bumped by reconnect() to force the subscribe effect to re-run after a fatal
  // stream error (native EventSource does not auto-reconnect on a non-200).
  const [nonce, setNonce] = useState(0);

  // Hold the live StreamHandle so the terminal-status effect can close it
  // without tearing down / re-running the subscribe effect.
  const handleRef = useRef<StreamHandle | null>(null);

  // A route change renders before effects reset the reducer. Never expose or
  // mirror another run's history during that render (especially its terminal state).
  const currentState = wrap.runId === runId ? wrap.state : initialEventState();
  const derivedStatus = currentState.derivedStatus;
  const terminal = derivedStatus ? isTerminal(derivedStatus) : false;

  // Mirror derived status into the run cache so the header updates live.
  useEffect(() => {
    if (!derivedStatus) return;
    qc.setQueryData<Run>(qk.run(runId), (prev) =>
      reconcileRunStatus(prev, derivedStatus),
    );
    // On terminal status, refetch the authoritative run so late-populated fields
    // (failure_reason / failure_message / finished_at / pr_url) land in the
    // header — the stream's optimistic status patch doesn't carry them.
    if (isTerminal(derivedStatus)) {
      qc.invalidateQueries({ queryKey: qk.run(runId) });
    }
  }, [derivedStatus, runId, qc]);

  // Late-populated fields land a moment AFTER the run goes terminal, while the
  // SSE stream is already closed, so the single terminal refetch above races
  // ahead of them. Poll the authoritative run a bounded few times until the
  // kind's "late field" arrives:
  //   - agent runs:  pr_url (the reconciler opens the draft PR post-terminal;
  //     readonly runs simply never find one and stop) — finding F11.
  //   - review runs: review_output (the runner posts REVIEW.md around the same
  //     moment the Job completes; without this the page said "No review output
  //     was produced" until a manual reload) — M6 live find.
  useEffect(() => {
    if (!terminal) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const settled = (run: Run | undefined): boolean => {
      if (!run) return false;
		if (run.delivery_status === 'pending') return false;
		if (run.delivery_status) return true;
      if (run.kind === 'review') return Boolean(run.review_output);
      return Boolean(run.pr_url);
    };
    [1000, 2000, 4000, 8000].forEach((delay) => {
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          if (settled(qc.getQueryData<Run>(qk.run(runId)))) return;
          void qc.invalidateQueries({ queryKey: qk.run(runId) });
        }, delay),
      );
    });
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [terminal, runId, qc]);

  // The draft PR link can also arrive on a later run.status frame carrying
  // pr_url/pr_number (not the full run); patch those onto the cached run too.
  const prURL = currentState.prURL;
  const prNumber = currentState.prNumber;
  const prDraft = currentState.prDraft;
  const prReadyAt = currentState.prReadyAt;
  const prState = currentState.prState;
  useEffect(() => {
    if (!prURL) return;
    qc.setQueryData<Run>(qk.run(runId), (prev) =>
      prev && (prev.pr_url !== prURL || prev.pr_draft !== prDraft || prev.pr_ready_at !== prReadyAt || prev.pr_state !== prState)
        ? { ...prev, pr_url: prURL, pr_number: prNumber ?? prev.pr_number ?? null, pr_draft: prDraft ?? prev.pr_draft, pr_ready_at: prReadyAt ?? prev.pr_ready_at, pr_state: prState ?? prev.pr_state }
        : prev,
    );
  }, [prURL, prNumber, prDraft, prReadyAt, prState, runId, qc]);

  // Close the stream once we've observed a terminal status. The server closes a
  // terminal SSE connection; if we leave the EventSource open the browser treats
  // that close as an error and auto-reconnects (~every 3s) forever, re-replaying
  // the run's whole history each time. Closing here stops that loop.
  useEffect(() => {
    if (terminal) {
      handleRef.current?.close();
      handleRef.current = null;
      setPhase('closed');
    }
  }, [terminal]);

  useEffect(() => {
    if (!enabled || !runId) return;
    // Don't (re)open a stream for an already-terminal run — the close effect
    // owns teardown and the server would just replay history and close again.
    if (terminal) return;
    let cancelled = false;
    let handle: StreamHandle | null = null;

    dispatch({ kind: 'reset', runId });
    setPhase('connecting');

    (async () => {
      // 1. Replay the complete backlog in bounded REST pages, then hydrate the
      // reducer once. This prevents 1,000+ event runs from falling through to
      // one-SSE-frame/one-render historical replay.
      let afterSeq = 0;
      const backlog: RunEvent[] = [];
      let replayComplete = false;
      try {
        while (true) {
          const page = await api.listEvents(runId, afterSeq, REPLAY_PAGE_SIZE);
          if (cancelled) return;
          backlog.push(...page);
          if (page.length < REPLAY_PAGE_SIZE) {
            replayComplete = true;
            break;
          }
          const nextSeq = page.reduce((highest, event) => Math.max(highest, event.seq), afterSeq);
          if (nextSeq <= afterSeq) break;
          afterSeq = nextSeq;
        }
      } catch {
        // Non-fatal: preserve every completed page and let SSE replay from the
        // last durable cursor rather than dropping already-loaded history.
      }
      if (cancelled) return;
      if (backlog.length) {
        const hydrated = reduceEvents(initialEventState(), backlog);
        dispatch({ kind: 'hydrate', runId, state: hydrated });
        afterSeq = hydrated.lastSeq;
        // Only a fully paged terminal backlog is authoritative. If a later page
        // failed, SSE still needs to replay the missing tail.
        if (
          replayComplete
          && hydrated.derivedStatus
          && isTerminal(hydrated.derivedStatus)
        ) {
          setPhase('closed');
          return;
        }
      }
      if (cancelled) return;

      // 2. Follow live from our cursor.
      handle = api.streamRun(runId, afterSeq, {
        onOpen: () => !cancelled && setPhase('live'),
        onFrame: (frame) => {
          if (cancelled) return;
          dispatch({ kind: 'events', runId, events: [frame.data] });
          // run.status frames may carry the full run object.
          const maybeRun = (frame.data as { run?: Run }).run;
          if (maybeRun?.id === runId) {
            qc.setQueryData<Run>(qk.run(runId), (prev) =>
              reconcileRunSnapshot(prev, maybeRun),
            );
          }
        },
        onError: () => {
          if (!cancelled) setPhase('error');
        },
      });
      handleRef.current = handle;
    })();

    return () => {
      cancelled = true;
      handle?.close();
      if (handleRef.current === handle) handleRef.current = null;
      setPhase('closed');
    };
    // `nonce` re-runs the effect for an explicit reconnect after a fatal error.
    // `terminal` is intentionally read (not a dep) to gate the initial open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, runId, enabled, qc, nonce]);

  // Re-subscribe after a fatal stream error (401/404/SSE-hostile proxy). The
  // native EventSource permanently closed; this opens a fresh one from our
  // current cursor.
  const reconnect = useCallback(() => setNonce((n) => n + 1), []);

  return {
    events: currentState.events,
    lastSeq: currentState.lastSeq,
    derivedStatus,
    phase: wrap.runId !== runId ? 'connecting' : terminal ? 'closed' : phase,
    terminal,
    reconnect,
  };
}
